import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { buildUrl, truncateKeywordSpaces, toQueryString } from 'utils';
import { Grid, Paper, Container, InputAdornment } from '@material-ui/core';
import { logsActions } from 'state/modules/logs';
import { useStyles } from './NotificationsList.styled';
import { FETCH_STATE } from 'constants';
import { useHistory } from 'react-router-dom';

import { useQuery } from 'hooks';
import { Search as SearchIcon } from '@material-ui/icons';
import { SearchField } from 'components/FormFields';
import { debounce } from 'lodash';
import { PartnersSelect } from 'components/AsyncFields';

import Layout from 'components/Layout';
import Title from 'components/Title';
import { Table } from 'components/UI';
import { removeEmpty } from 'utils';
import moment from 'moment';

const NotificationsList = () => {
  const urlParams = useQuery();
  const defaultParams = {
    shared: true,
    target_type: 'OrgToOrgAccess',
    include_secondary: true,
  };
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();

  // We use multiple selectors to avoid table rerender / consider using reselect.
  const data = useSelector((state) => state.logs.logs);
  const fetchingLogs = useSelector((state) => state.logs.fetchingLogs);
  const pagination = useSelector((state) => state.logs.pagination);
  const localeFormat = useSelector((state) => state.organizations.localeFormat);

  const loading = fetchingLogs === FETCH_STATE.LOADING || fetchingLogs === FETCH_STATE.UNSENT;

  const fetchNotifications = useCallback(
    debounce((params = {}) => {
      dispatch(logsActions.fetchLogs({ ...params, ...defaultParams }));
    }, 1000),
    [dispatch]
  );

  // Fetch on load/ params change
  useEffect(() => {
    fetchNotifications(params);
  }, [fetchNotifications, params]);

  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    updateParams(newParams);
  };

  const handleSearch = (name, value) => {
    const newParams = {
      ...params,
      page: 1,
      [name]: truncateKeywordSpaces(value),
    };
    updateParams(newParams);
  };

  const handlePageChange = (page) => {
    const newParams = { ...params, page };
    updateParams(newParams);
  };

  const handlePerRowsChange = async (limit) => {
    const newParams = { ...params, page: 1, limit };
    updateParams(newParams);
  };

  const updateParams = (newParams) => {
    const cleanParam = removeEmpty(newParams);
    const qs = toQueryString(cleanParam);
    history.replace(buildUrl(`/notifications${qs}`));
    setParams(cleanParam);
  };

  const columns = [
    {
      name: i18n.t('notifications.subject'),
      selector: 'subject',
      format: (row) => row?.subject || '--',
      grow: 4,
    },
    {
      name: i18n.t('notifications.partner'),
      selector: 'partner',
      format: (row) => row?.organization?.name || '--',
      grow: 1,
    },
    {
      name: i18n.t('common.date'),
      selector: 'date',
      format: (row) =>
        row?.timestamp ? moment(row?.timestamp).format(`${localeFormat} hh:mm A`) : '--',
      minWidth: '9rem',
      grow: 1,
    },
  ];

  return (
    <Layout title={i18n.t('notifications.title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center">
              <Grid item style={{ marginRight: 'auto' }}>
                <Title title={i18n.t('notifications.title')} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="flex-end"
              style={{ marginBottom: '25px' }}
            >
              <Grid item xs={3}>
                <SearchField
                  margin="none"
                  label={i18n.t('notifications.search')}
                  placeholder={i18n.t('notifications.search_placeholder')}
                  field={{ onChange: handleSearch, name: 'keyword', value: params.keyword || '' }}
                  inputProps={{
                    startAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.searchIcon,
                        }}
                        position="start"
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  data-cy="search-notifications-input"
                />
              </Grid>
              <Grid item xs={3}>
                <PartnersSelect
                  form={{ setFieldValue: handleSearch }}
                  label={i18n.t('notifications.search_by_partner')}
                  field={{
                    name: 'organization_id',
                    value: params.organization_id,
                  }}
                  margin="none"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={data}
                columns={columns}
                onSort={handleSort}
                loading={loading}
                handlePageChange={handlePageChange}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
                page={params?.page}
                limit={params?.limit}
                defaultSortField={params?.sort_field}
                defaultSortAsc={!params?.sort_desc}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export { NotificationsList };
